/**
 * Questo file definisce il componente React per la sessione di studio pubblica.
 * - È molto simile a `StudySession.js` ma gestisce la logica per le cartelle pubbliche.
 * - Effettua il dispatch di un'azione Redux per caricare i dati della cartella pubblica dal backend.
 * - Pulisce lo stato della cartella pubblica corrente quando il componente viene smontato.
 * - Riutilizza gli stessi stili CSS di `StudySession.css` per un'esperienza utente coerente.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
//import { useParams, Link, useNavigate } from 'react-router-dom'; //DA ELIMINARE se non serve
import { useParams, Link } from 'react-router-dom';
import classNames from 'classnames';
import { fetchPublicFolderDetails, clearCurrentPublicFolder } from '../redux/flashcardSlice';
import './StudySession.css'; // Riutilizzo degli stili

/**
 * FUNZIONE DI UTILITÀ per mescolare un array.
 * - Implementa l'algoritmo di Fisher-Yates (noto anche come Knuth shuffle).
 * - Garantisce che le card siano presentate in un ordine casuale.
 */
const shuffleArray = (array) => {
    let currentIndex = array.length, randomIndex;
    while (currentIndex !== 0) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;
        [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }
    return array;
};

/**
 * COMPONENTE PRINCIPALE: PublicStudySession
 * - Gestisce l'interfaccia e la logica di una sessione di studio per una cartella pubblica.
 */
export default function PublicStudySession() {
    // Estrae l'ID della cartella dai parametri dell'URL.
    const { folderId } = useParams();
    //const navigate = useNavigate(); //DA ELIMINARE se non serve
    // Hook per inviare azioni a Redux.
    const dispatch = useDispatch();

    /**
     * ESTRAZIONE DATI DALLO STORE REDUX
     * - `folder`: I dettagli della cartella pubblica corrente.
     * - `loading`: Lo stato di caricamento dal backend.
     */
    const folder = useSelector(state => state.flashcards.currentPublicFolder);
    const loading = useSelector(state => state.flashcards.loading);

    /**
     * STATI DEL COMPONENTE
     * - Simili a `StudySession.js` per gestire il flusso della sessione.
     * - `sessionState`: Aggiunge lo stato 'empty' per gestire le cartelle senza card.
     */
    const [studyDeck, setStudyDeck] = useState([]);
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    const [results, setResults] = useState([]);
    const [sessionState, setSessionState] = useState('loading');

    /**
     * EFFETTO: Caricamento dei dati della cartella.
     * - Eseguito al montaggio del componente e se `folderId` cambia.
     * - Fa il dispatch dell'azione `fetchPublicFolderDetails` per recuperare i dati.
     * - La funzione di pulizia (`return`) fa il dispatch di `clearCurrentPublicFolder`
     * quando il componente viene smontato, per evitare di mostrare dati vecchi.
     */
    useEffect(() => {
        if (folderId) {
            dispatch(fetchPublicFolderDetails(folderId));
        }
        return () => {
            dispatch(clearCurrentPublicFolder());
        };
    }, [dispatch, folderId]);

    /**
     * EFFETTO: Inizializzazione della sessione di studio dopo il caricamento.
     * - Eseguito quando `folder`, `loading`, o `folderId` cambiano.
     * - Quando il caricamento è terminato e la cartella corretta è disponibile:
     * - Se ha flashcard, le mescola e avvia la sessione.
     * - Altrimenti, imposta lo stato su 'empty'.
     */
    useEffect(() => {
        if (folder && !loading && folder.id.toString() === folderId) {
            if (folder.flashcards && folder.flashcards.length > 0) {
                setStudyDeck(shuffleArray([...folder.flashcards]));
                setSessionState('studying');
            } else {
                setSessionState('empty');
            }
        }
    }, [folder, loading, folderId]);

    // Memoizzazione della card corrente per ottimizzazione.
    const currentCard = useMemo(() => {
        return studyDeck[currentCardIndex];
    }, [studyDeck, currentCardIndex]);

    // Gestore per girare la card.
    const handleFlip = () => setIsFlipped(!isFlipped);

    // Gestore per registrare la risposta e avanzare.
    const handleAnswer = (isCorrect) => {
        setResults([...results, { cardId: currentCard.id, correct: isCorrect }]);
        if (currentCardIndex < studyDeck.length - 1) {
            setCurrentCardIndex(currentCardIndex + 1);
            setIsFlipped(false);
        } else {
            setSessionState('finished');
        }
    };
    
    // Gestore per ricominciare la sessione (tutte le card o solo gli errori).
    const handleRestart = (onlyMistakes = false) => {
        let newDeck;
        if (onlyMistakes) {
            const mistakeIds = results.filter(r => !r.correct).map(r => r.cardId);
            newDeck = folder.flashcards.filter(card => mistakeIds.includes(card.id));
        } else {
            newDeck = [...folder.flashcards];
        }
        setStudyDeck(shuffleArray(newDeck));
        setCurrentCardIndex(0);
        setIsFlipped(false);
        setResults([]);
        setSessionState('studying');
    };

    // --- VISTE CONDIZIONALI RENDERIZZATE ---

    // Vista di caricamento.
    if (sessionState === 'loading') {
        return <div className="study-session-loading">Caricamento sessione...</div>;
    }
    
    // Vista per cartella vuota.
    if (sessionState === 'empty') {
         return (
            <div className="study-session-container">
                <div className="results-screen">
                    <h2>Cartella Vuota</h2>
                    <p>Questa cartella non contiene flashcard da studiare.</p>
                    <Link to="/public" className="btn btn-primary">Torna all'elenco</Link>
                </div>
            </div>
        );
    }

    // Vista finale con i risultati.
    if (sessionState === 'finished') {
        const correctAnswers = results.filter(r => r.correct).length;
        const totalAnswers = results.length;
        const score = totalAnswers > 0 ? Math.round((correctAnswers / totalAnswers) * 100) : 0;

        return (
            <div className="study-session-container">
                <div className="results-screen">
                    <h2>Sessione Completata!</h2>
                    <p className="score">Punteggio: {score}%</p>
                    <p>Hai risposto correttamente a {correctAnswers} su {totalAnswers} domande.</p>
                    <div className="results-actions">
                        <button onClick={() => handleRestart(false)} className="btn btn-primary">Ricomincia</button>
                        {correctAnswers < totalAnswers && (
                            <button onClick={() => handleRestart(true)} className="btn btn-outline-secondary">Studia solo quelle sbagliate</button>
                        )}
                        <Link to="/public" className="btn btn-outline-secondary">Torna all'elenco</Link>
                    </div>
                </div>
            </div>
        );
    }

    // Vista principale della sessione di studio.
    return (
        <div className="study-session-container">
            {/* Header con link per tornare alla pagina di dettaglio della cartella. */}
            <div className="study-header">
                <Link to={`/public/folder/${folderId}`} className="exit-button" title="Esci dalla sessione">
                    <i className="bi bi-x-lg"></i>
                </Link>
                <div className="progress-bar-container">
                    <div 
                        className="progress-bar-fill" 
                        style={{ width: `${((currentCardIndex + 1) / studyDeck.length) * 100}%` }}
                    ></div>
                </div>
                <div className="progress-text">{currentCardIndex + 1} / {studyDeck.length}</div>
            </div>
            {/* Area della flashcard */}
            <div className="study-card-area">
                <div className={classNames('study-card-scene', { 'is-flipped': isFlipped })}>
                    <div className="study-card-face study-card-front">
                        <div className="card-content-study">
                            <small>Domanda</small>
                            <p>{currentCard.domanda}</p>
                        </div>
                    </div>
                    <div className="study-card-face study-card-back">
                        <div className="card-content-study">
                            <small>Risposta</small>
                            <p>{currentCard.risposta}</p>
                        </div>
                    </div>
                </div>
            </div>
            {/* Controlli della sessione */}
            <div className="study-controls">
                {!isFlipped ? (
                    <button onClick={handleFlip} className="btn btn-primary btn-lg flip-button">Gira la card</button>
                ) : (
                    <div className="answer-buttons">
                        <button onClick={() => handleAnswer(false)} className="btn btn-danger btn-lg">Sbagliato</button>
                        <button onClick={() => handleAnswer(true)} className="btn btn-success btn-lg">Corretto</button>
                    </div>
                )}
            </div>
        </div>
    );
}